/**
 * Verification Status Hook
 * Custom hook for managing user verification status and trust system
 */

import { useState, useEffect, useCallback } from 'react';
import {
    VerificationStatus,
    DocumentVerification,
    TrustSystem,
    VerificationBenefits,
    DocumentType,
    TrustScoreEntry
} from '../types/verification';
import { verificationService } from '../services/verificationService';
import { TRUST_LEVELS } from '../constants/verification';
import { useAuth } from '../contexts/AuthProvider';
import { useVerificationSocket } from './useVerificationSocket';

interface VerificationStatusData {
    status: VerificationStatus;
    documents: DocumentVerification[];
    trustSystem: TrustSystem;
    benefits: VerificationBenefits;
    requiredDocuments: DocumentType[];
    canUploadMore: boolean;
    documentsCount?: {
        total: number;
        verified: number;
        pending: number;
        rejected: number;
    };
    nextSteps?: string[];
}

// Global state management for verification data
interface GlobalVerificationState {
    data: VerificationStatusData | null;
    isLoading: boolean;
    error: string | null;
    lastFetchTime: number;
    fetchPromise: Promise<any> | null;
}

const globalState: GlobalVerificationState = {
    data: null,
    isLoading: false,
    error: null,
    lastFetchTime: 0,
    fetchPromise: null
};

const FETCH_DEBOUNCE_MS = 1000; // Minimum time between fetches
const SOCKET_REFRESH_DEBOUNCE_MS = 5000; // Minimum time between socket-triggered refreshes
const stateListeners = new Set<(state: GlobalVerificationState) => void>();
let lastSocketRefreshTime = 0;

const updateGlobalState = (updates: Partial<GlobalVerificationState>) => {
    console.log('🔄 GLOBAL STATE UPDATE:', {
        before: {
            status: globalState.data?.status,
            documentsCount: globalState.data?.documentsCount,
            isLoading: globalState.isLoading
        },
        updates: {
            status: updates.data?.status,
            documentsCount: updates.data?.documentsCount,
            isLoading: updates.isLoading
        }
    });
    Object.assign(globalState, updates);
    console.log('🔄 NOTIFYING LISTENERS:', stateListeners.size, 'listeners');
    stateListeners.forEach(listener => listener(globalState));
};

export const useVerificationStatus = () => {
    const { isAuthenticated } = useAuth();
    const { subscribeToStatusUpdates } = useVerificationSocket();
    const [data, setData] = useState<VerificationStatusData | null>(globalState.data);
    const [trustHistory, setTrustHistory] = useState<TrustScoreEntry[]>([]);
    const [isLoading, setIsLoading] = useState(globalState.isLoading);
    const [error, setError] = useState<string | null>(globalState.error);
    const [refreshing, setRefreshing] = useState(false);

    // Subscribe to global state changes
    useEffect(() => {
        const listener = (state: GlobalVerificationState) => {
            console.log('🔄 HOOK LISTENER RECEIVED STATE UPDATE:', {
                status: state.data?.status,
                documentsCount: state.data?.documentsCount,
                isLoading: state.isLoading,
                error: state.error
            });
            setData(state.data);
            setIsLoading(state.isLoading);
            setError(state.error);
        };

        console.log('🔄 HOOK REGISTERING STATE LISTENER');
        stateListeners.add(listener);
        return () => {
            console.log('🔄 HOOK UNREGISTERING STATE LISTENER');
            stateListeners.delete(listener);
        };
    }, []);



    // Fetch verification status
    const fetchVerificationStatus = useCallback(async (silent = false, forceRefresh = false) => {
        // Skip fetching if user is not authenticated
        if (!isAuthenticated) {
            updateGlobalState({ isLoading: false });
            return;
        }

        const now = Date.now();

        // Debounce: check if we just fetched recently (skip debounce if forceRefresh is true)
        if (!silent && !forceRefresh && now - globalState.lastFetchTime < FETCH_DEBOUNCE_MS) {
            console.log(`🔄 Skipping fetch due to debounce (${now - globalState.lastFetchTime}ms ago)`);
            return;
        }

        // If there's already a fetch in progress and we're not forcing, wait for it instead of making a new request
        if (globalState.fetchPromise && !forceRefresh) {
            console.log('🔄 Waiting for existing fetch to complete...');
            try {
                await globalState.fetchPromise;
                return;
            } catch (error) {
                // If the existing fetch failed, we'll proceed with a new one
                console.log('🔄 Existing fetch failed, proceeding with new fetch');
                updateGlobalState({ fetchPromise: null });
            }
        } else if (globalState.fetchPromise && forceRefresh) {
            // Force refresh should not create request storms: wait for in-flight request and only continue
            // if data is still stale afterwards.
            console.log('🔄 Force refresh requested while fetch in progress, waiting for current fetch');
            try {
                await globalState.fetchPromise;
            } catch {
                // Continue to next fetch attempt if current one failed.
            }

            if (Date.now() - globalState.lastFetchTime < FETCH_DEBOUNCE_MS) {
                console.log('🔄 Recent fetch already completed, skipping extra force refresh');
                return;
            }
        }

        if (!silent) updateGlobalState({ isLoading: true, error: null });

        // Create a new fetch promise and store it globally
        const fetchPromise = (async () => {
            try {
                const [statusResponse, requirementsResponse] = await Promise.all([
                    verificationService.getVerificationStatus(),
                    verificationService.getVerificationRequirements()
                ]);

                if (statusResponse.success && statusResponse.data) {
                    const { verification, trustSystem, benefits, status, requiredDocuments: apiRequiredDocuments, canUploadMore } = statusResponse.data;
                    console.log('API response verification data:', verification);

                    const requiredDocuments = Array.isArray(requirementsResponse?.requiredDocuments)
                        ? requirementsResponse.requiredDocuments
                        : apiRequiredDocuments;

                    // Fetch actual user documents
                    let documents = [];
                    try {
                        const documentsResponse = await verificationService.getUserDocuments();
                        if (documentsResponse.success) {
                            documents = documentsResponse.documents || [];
                        }
                    } catch (docError) {
                        console.warn('Failed to fetch user documents:', docError);
                        // Keep documents as empty array if fetch fails
                    }

                    // Use API provided document counts (they are now correct and real-time)
                    console.log('📊 Using API provided counts:', JSON.stringify(verification?.documentsCount, null, 2));

                    // Only try to calculate if documents are available, otherwise trust API counts
                    const shouldUseCalculated = documents.length > 0;
                    let finalDocumentsCount = verification?.documentsCount;

                    if (shouldUseCalculated) {
                        const calculatedCounts = {
                            total: documents.length,
                            verified: documents.filter(doc => doc.status === 'verified').length,
                            pending: documents.filter(doc => doc.status === 'pending' || doc.status === 'under_review').length,
                            rejected: documents.filter(doc => doc.status === 'rejected').length
                        };
                        console.log('📊 Using calculated counts from documents:', JSON.stringify(calculatedCounts, null, 2));
                        finalDocumentsCount = calculatedCounts;
                    } else {
                        console.log('📊 Documents array empty, using API provided counts as fallback');
                    }

                    const verificationData: VerificationStatusData = {
                        status: verification?.overallStatus || status || 'unverified',
                        documents: Array.isArray(documents) ? documents : [],
                        trustSystem: trustSystem || {
                            currentScore: 0,
                            level: 'new',
                            scoreHistory: [],
                            accessLimits: {
                                maxGigValue: 0,
                                maxApplicationValue: 0,
                                canPostUnlimited: false,
                                canApplyUnlimited: false
                            },
                            performanceMetrics: {
                                completedGigs: 0,
                                averageRating: 0,
                                disputeRate: 0,
                                responseTime: 0,
                                completionRate: 0
                            }
                        },
                        benefits: benefits || {
                            hasVerifiedBadge: false,
                            feeReduction: 0,
                            canPostUnlimitedGigs: false,
                            canApplyUnlimitedGigs: false,
                            hasPrioritySupport: false,
                            accessToPremiumCategories: false,
                            isFeatured: false,
                            hasEarlyFeatureAccess: false
                        },
                        requiredDocuments,
                        canUploadMore: canUploadMore ?? verification?.overallStatus !== 'verified',
                        documentsCount: finalDocumentsCount,
                        nextSteps: verification?.nextSteps || []
                    };
                    console.log('🔄 Verification data updated with counts:', {
                        status: verificationData.status,
                        documentsCount: verificationData.documentsCount,
                        totalDocuments: verificationData.documents.length
                    });
                    updateGlobalState({
                        data: verificationData,
                        error: null,
                        lastFetchTime: now
                    });
                } else {
                    updateGlobalState({ error: statusResponse.message || 'Failed to load verification status' });
                }
            } catch (error: any) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to load verification status';
                updateGlobalState({ error: errorMessage });
                console.error('Error fetching verification status:', {
                    message: errorMessage,
                    status: error.response?.status,
                    data: error.response?.data,
                    error
                });
                throw error; // Re-throw to be caught by promise wrapper
            } finally {
                updateGlobalState({ isLoading: false, fetchPromise: null });
            }
        })();

        updateGlobalState({ fetchPromise });

        try {
            await fetchPromise;
        } catch (error) {
            // Error already handled in the promise
        }
    }, [isAuthenticated]);

    // Fetch trust score history
    const fetchTrustHistory = useCallback(async () => {
        // Skip fetching if user is not authenticated
        if (!isAuthenticated) {
            return;
        }

        try {
            const response = await verificationService.getTrustScoreHistory();

            if (response.success) {
                setTrustHistory(response.data.history);
            }
        } catch (error: any) {
            console.error('Error fetching trust history:', error);
        }
    }, [isAuthenticated]);

    // Refresh data (for pull-to-refresh or manual refresh)
    const refresh = useCallback(async () => {
        setRefreshing(true);
        try {
            // Add a small delay to ensure backend changes are fully propagated
            await new Promise(resolve => setTimeout(resolve, 500));

            await Promise.all([
                fetchVerificationStatus(false, true), // Force refresh to bypass any caching or debouncing
                fetchTrustHistory()
            ]);
            console.log('Refresh completed successfully, data updated:', data);
        } catch (error) {
            console.error('Error during refresh:', error);
        } finally {
            setRefreshing(false);
        }
    }, [fetchVerificationStatus, fetchTrustHistory, data]);

    // Get current trust level information
    const getCurrentTrustLevel = useCallback(() => {
        if (!data) return null;

        const currentScore = data.trustSystem.currentScore;
        return TRUST_LEVELS.find(level =>
            currentScore >= level.minScore && currentScore <= level.maxScore
        ) || TRUST_LEVELS[0];
    }, [data]);

    // Get next trust level information
    const getNextTrustLevel = useCallback(() => {
        if (!data) return null;

        const currentScore = data.trustSystem.currentScore;
        return TRUST_LEVELS.find(level => level.minScore > currentScore);
    }, [data]);

    // Calculate progress to next level
    const getProgressToNextLevel = useCallback(() => {
        if (!data) return 0;

        const currentScore = data.trustSystem.currentScore;
        const currentLevel = TRUST_LEVELS.find(level =>
            currentScore >= level.minScore &&
            (level.maxScore ? currentScore <= level.maxScore : true)
        );
        const nextLevel = TRUST_LEVELS.find(level => level.minScore > currentScore);

        if (!currentLevel || !nextLevel) return 100;

        const levelRange = nextLevel.minScore - currentLevel.minScore;
        const progress = (currentScore - currentLevel.minScore) / levelRange;

        return Math.min(Math.max(progress * 100, 0), 100);
    }, [data]);

    // Get verification completion percentage
    const getVerificationProgress = useCallback(() => {
        if (!data || !data.documents) return 0;

        // If the overall status is verified, return 100%
        if (data.status === 'verified') return 100;

        // Get all unique categories that have verified documents
        const verifiedCategories = new Set(
            data.documents
                .filter(doc => doc.status === 'verified')
                .map(doc => doc.documentCategory || doc.category) // Support both field names
        );

        // Get all unique categories (total available)
        const totalCategories = new Set(
            data.documents.map(doc => doc.documentCategory || doc.category) // Support both field names
        );

        // If no documents uploaded, return 0
        if (totalCategories.size === 0) return 0;

        // Calculate percentage based on verified categories vs total categories
        return (verifiedCategories.size / totalCategories.size) * 100;
    }, [data]);

    // Check if user can post/apply for a gig of specific value
    const checkGigPermission = useCallback(async (action: 'post_gig' | 'apply_gig', value: number) => {
        try {
            return await verificationService.checkPermission(action, value);
        } catch (error) {
            console.error('Error checking gig permission:', error);
            return {
                allowed: false,
                reason: 'Unable to check permissions at this time'
            };
        }
    }, []);

    // Get documents by status
    const getDocumentsByStatus = useCallback((status: VerificationStatus) => {
        return data?.documents?.filter(doc => doc.status === status) || [];
    }, [data]);

    // Get documents by category
    const getDocumentsByCategory = useCallback((category: 'identity' | 'address' | 'business') => {
        return data?.documents?.filter(doc => (doc.documentCategory || doc.category) === category) || [];
    }, [data]);

    // Check if specific document type is uploaded
    const hasDocumentType = useCallback((documentType: DocumentType) => {
        return data?.documents?.some(doc => doc.documentType === documentType) || false;
    }, [data]);

    // Get missing required documents
    const getMissingRequiredDocuments = useCallback(() => {
        if (!data || !data.requiredDocuments || !data.documents) return [];

        return data.requiredDocuments.filter(documentType =>
            !data.documents.some(doc =>
                doc.documentType === documentType &&
                ['verified', 'pending', 'under_review'].includes(doc.status)
            )
        );
    }, [data]);

    // Check if user is fully verified
    const isFullyVerified = useCallback(() => {
        return data?.status === 'verified';
    }, [data]);

    // Check if user has any benefits
    const hasAnyBenefits = useCallback(() => {
        return data?.benefits && Object.values(data.benefits).some(benefit => benefit);
    }, [data]);

    // Listen for socket verification status updates and refresh data
    useEffect(() => {
        if (!isAuthenticated) return;

        let refreshTimeout: NodeJS.Timeout | null = null;
        let isSubscribed = true;

        console.log('🔗 VERIFICATION STATUS HOOK - Setting up socket subscription...');

        const unsubscribe = subscribeToStatusUpdates((update) => {
            if (!isSubscribed) {
                console.log('🔄 VERIFICATION STATUS HOOK - Ignoring update, subscription is inactive');
                return;
            }

            const now = Date.now();
            if (now - lastSocketRefreshTime < SOCKET_REFRESH_DEBOUNCE_MS) {
                console.log('🔄 VERIFICATION STATUS HOOK - Skipping socket refresh due to debounce');
                return;
            }
            lastSocketRefreshTime = now;

            console.log('🔄 VERIFICATION STATUS HOOK - Socket verification status update received:', update);
            console.log('🔄 VERIFICATION STATUS HOOK - Document ID:', update.documentId);
            console.log('🔄 VERIFICATION STATUS HOOK - New Status:', update.status);
            console.log('🔄 VERIFICATION STATUS HOOK - Trust Score Change:', update.trustScoreChange);
            console.log('🔄 VERIFICATION STATUS HOOK - Current global data before refresh:', globalState.data?.status, globalState.data?.documentsCount);

            // Clear any existing timeout to prevent multiple refreshes
            if (refreshTimeout) {
                console.log('🔄 VERIFICATION STATUS HOOK - Clearing existing refresh timeout');
                clearTimeout(refreshTimeout);
            }

            // Force immediate refresh with bypassing debounce and cache
            refreshTimeout = setTimeout(() => {
                if (!isSubscribed) {
                    console.log('🔄 VERIFICATION STATUS HOOK - Skipping refresh, subscription is inactive');
                    return;
                }

                console.log('🔄 VERIFICATION STATUS HOOK - Executing immediate verification data refresh after socket update...');

                fetchVerificationStatus(false, true).then(() => {
                    console.log('✅ VERIFICATION STATUS HOOK - Verification data refreshed successfully after socket update');
                    console.log('✅ VERIFICATION STATUS HOOK - Updated global state data:', globalState.data?.status, globalState.data?.documentsCount);
                    console.log('✅ VERIFICATION STATUS HOOK - Total documents:', globalState.data?.documentsCount?.total);
                    console.log('✅ VERIFICATION STATUS HOOK - Verified documents:', globalState.data?.documentsCount?.verified);
                    console.log('✅ VERIFICATION STATUS HOOK - Rejected documents:', globalState.data?.documentsCount?.rejected);
                }).catch((err) => {
                    console.error('❌ VERIFICATION STATUS HOOK - Failed to refresh verification data:', err);
                });
                refreshTimeout = null;
            }, 1200); // Delay slightly to let backend aggregation complete and avoid burst fetches
        });

        console.log('🔗 VERIFICATION STATUS HOOK - Socket subscription established');

        return () => {
            console.log('🔓 VERIFICATION STATUS HOOK - Cleaning up socket subscription');
            isSubscribed = false;
            if (refreshTimeout) {
                clearTimeout(refreshTimeout);
            }
            unsubscribe();
        };
    }, [isAuthenticated, subscribeToStatusUpdates, fetchVerificationStatus]);

    // Initial data load
    useEffect(() => {
        if (isAuthenticated) {
            fetchVerificationStatus();
            fetchTrustHistory();
        }
    }, [fetchVerificationStatus, fetchTrustHistory, isAuthenticated]);

    // Memoized computed values
    const currentTrustLevel = getCurrentTrustLevel();
    const nextTrustLevel = getNextTrustLevel();
    const progressToNextLevel = getProgressToNextLevel();
    const verificationProgress = getVerificationProgress();
    const missingRequiredDocuments = getMissingRequiredDocuments();
    const fullyVerified = isFullyVerified();
    const anyBenefits = hasAnyBenefits();

    return {
        // Legacy compatibility for Layout component
        verification: data ? {
            overallStatus: data.status,
            trustScore: data.trustSystem?.currentScore || 0,
            ...data
        } : null,

        // Data
        data,
        trustHistory,
        isLoading,
        error,
        refreshing,

        // Actions
        refresh,
        fetchVerificationStatus,
        checkGigPermission,

        // Computed values
        currentTrustLevel,
        nextTrustLevel,
        progressToNextLevel,
        verificationProgress,
        missingRequiredDocuments,
        isFullyVerified: fullyVerified,
        hasAnyBenefits: anyBenefits,

        // Utilities
        getDocumentsByStatus,
        getDocumentsByCategory,
        hasDocumentType
    };
};